/*
 * Design: Command Center — Dark Operations Dashboard
 * The Pulse Follow-Up System — homepage teaser section
 * Direct response: Problem-first framing, leads into /pulse sales page
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import {
  Flame,
  Thermometer,
  Snowflake,
  Brain,
  Shuffle,
  MessageSquare,
  Clock,
  ArrowRight,
  ChevronDown,
} from "lucide-react";

const buckets = [
  {
    id: "hot",
    icon: Flame,
    title: "Hot Bucket",
    period: "Days 1–30",
    color: "from-red-500 to-orange-500",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/20",
    iconColor: "text-red-400",
    frequency: "Every 1–4 days",
    description:
      "Speed, persistence, and timing. Pulse reaches out every 1–4 days with randomized intervals so it never feels robotic. AI texts and AI phone calls — all context-aware, all pushing toward booking.",
  },
  {
    id: "warm",
    icon: Thermometer,
    title: "Warm Bucket",
    period: "Days 30–119",
    color: "from-amber-500 to-yellow-500",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/20",
    iconColor: "text-amber-400",
    frequency: "Every 10–14 days",
    description:
      "These leads showed interest but didn't book. They're not dead — just not ready. Pulse stays present with spaced, intelligent outreach that respects the buyer's timeline while keeping you top of mind.",
  },
  {
    id: "cold",
    icon: Snowflake,
    title: "Cold Bucket",
    period: "Day 120+",
    color: "from-blue-500 to-cyan-500",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/20",
    iconColor: "text-blue-400",
    frequency: "Every 30–75 days",
    description:
      "Most systems abandon cold leads. Pulse does not. Buyers come back when timing, budget, or circumstances change. Pulse is there when that happens — quietly, consistently, automatically.",
  },
];

const differentiators = [
  {
    icon: Brain,
    title: "Context-Aware AI",
    description: "Every message and call analyzes previous conversations, tone, and direction — then crafts a natural, human-sounding nudge.",
  },
  {
    icon: Shuffle,
    title: "Randomized Timing",
    description: "Intervals are randomized every time. No predictable patterns. No robotic cadence. It feels like a real person following up.",
  },
  {
    icon: MessageSquare,
    title: "Multi-Channel",
    description: "AI-generated text messages and AI phone calls, randomly chosen. Pulse switches channels to maximize engagement.",
  },
  {
    icon: Clock,
    title: "Only Two Exits",
    description: "A lead only leaves when they book an appointment or explicitly opt out. Nothing else ends the follow-up. Period.",
  },
];

export default function PulseSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [expandedBucket, setExpandedBucket] = useState<string | null>("hot");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll("[data-animate]");
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = "1";
                (item as HTMLElement).style.transform = "translateY(0)";
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="pulse" className="relative py-24 sm:py-32">
      <div className="gradient-divider mb-24" />
      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header — Problem-first */}
        <div className="max-w-3xl mb-16">
          <p
            data-animate
            className="text-sm font-semibold uppercase tracking-widest text-purple-400 mb-3 opacity-0 translate-y-5 transition-all duration-500 ease-out"
          >
            GHL Workflow
          </p>
          <h2
            data-animate
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4 opacity-0 translate-y-5 transition-all duration-500 ease-out"
          >
            The Pulse Follow-Up System
          </h2>
          <p
            data-animate
            className="text-xl sm:text-2xl font-medium text-zinc-400 mb-4 opacity-0 translate-y-5 transition-all duration-500 ease-out"
          >
            A follow-up engine that refuses to let leads die.
          </p>
          <p
            data-animate
            className="text-zinc-300 text-lg leading-relaxed opacity-0 translate-y-5 transition-all duration-500 ease-out"
          >
            You're already paying for leads. But without relentless, intelligent follow-up, most of them silently die. Pulse works every single lead — AI texts, AI calls, context-aware nudges — until they book or opt out. Nothing else ends the follow-up.
          </p>
        </div>

        {/* Three Buckets */}
        <div
          data-animate
          className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-20 opacity-0 translate-y-5 transition-all duration-700 ease-out"
        >
          {buckets.map((bucket) => {
            const Icon = bucket.icon;
            const isExpanded = expandedBucket === bucket.id;
            return (
              <div
                key={bucket.id}
                className={`rounded-2xl bg-[#18181B] border transition-all duration-300 overflow-hidden ${
                  isExpanded ? bucket.borderColor : "border-white/5"
                }`}
              >
                <button
                  onClick={() => setExpandedBucket(isExpanded ? null : bucket.id)}
                  className="w-full p-6 flex items-center justify-between lg:flex-col lg:items-start lg:justify-start text-left"
                >
                  <div className="flex items-center gap-4 lg:mb-4">
                    <div className={`w-11 h-11 rounded-xl ${bucket.bgColor} flex items-center justify-center shrink-0`}>
                      <Icon size={22} className={bucket.iconColor} />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">{bucket.title}</h3>
                      <p className="text-zinc-500 text-sm">{bucket.period}</p>
                    </div>
                  </div>
                  <ChevronDown
                    size={18}
                    className={`text-zinc-500 transition-transform duration-200 lg:hidden ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  className={`px-6 pb-6 transition-all duration-300 ${
                    isExpanded ? "block" : "hidden lg:block"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`h-px flex-1 bg-gradient-to-r ${bucket.color} opacity-20`} />
                    <span className={`text-xs font-semibold uppercase tracking-wider ${bucket.iconColor}`}>
                      {bucket.frequency}
                    </span>
                    <div className={`h-px flex-1 bg-gradient-to-l ${bucket.color} opacity-20`} />
                  </div>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    {bucket.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Flow Arrow Connector (desktop only) */}
        <div
          data-animate
          className="hidden lg:flex items-center justify-center gap-3 -mt-16 mb-16 opacity-0 translate-y-5 transition-all duration-500 ease-out"
        >
          <div className="flex items-center gap-2 text-zinc-600 text-sm">
            <span className="text-red-400">Hot</span>
            <ArrowRight size={14} className="text-zinc-600" />
            <span className="text-zinc-500">30-Day Intelligence Scan</span>
            <ArrowRight size={14} className="text-zinc-600" />
            <span className="text-amber-400">Warm</span>
            <span className="text-zinc-600">/</span>
            <span className="text-blue-400">Cold</span>
          </div>
        </div>

        {/* Differentiators Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6 mb-12">
          {differentiators.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                data-animate
                className="flex gap-4 p-5 rounded-xl bg-[#18181B]/50 border border-white/5 opacity-0 translate-y-5 transition-all duration-500 ease-out"
              >
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                  <Icon size={20} className="text-purple-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                  <p className="text-zinc-400 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div
          data-animate
          className="text-center opacity-0 translate-y-5 transition-all duration-500 ease-out"
        >
          <p className="text-zinc-300 text-lg mb-6">
            You already paid for the leads. <span className="text-white font-semibold">Pulse makes sure you actually get paid from them.</span>
          </p>
          <Link
            href="/pulse"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-lg hover:shadow-purple-500/25 group"
            style={{
              background: "linear-gradient(135deg, #7C3AED, #D946EF)",
            }}
          >
            See Pricing & Get Pulse
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
